import React, { useEffect } from "react";
import { Header, StyledBackButtonIcon, StyledSpace, BackButton } from "./style";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { TrainerDetailsProps } from "../../../../../../interfaces/screens/trainer-details";
import { useAppDispatch } from "../../../../../../store/config";
import {
  getTrainerStart,
  resetTrainer,
} from "../../../../../../store/features/trainer/trainer-slice";
import { useTranslation } from "react-i18next";
import { StyledBreadcrumb } from "../../../../../../components/common/styles";

export const TrainerDetailsHeader: React.FC<TrainerDetailsProps> = ({
  trainerName,
  trainingProgramName,
  workoutName,
  exerciseName,
  techniqueVideoName,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: trainerId } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (trainerId) {
      dispatch(getTrainerStart({ trainerId: trainerId || "" }));
    }
    return () => {
      dispatch(resetTrainer());
    };
  }, [dispatch, trainerId]);

  const mapSegmentToName = (
    segment: string,
    index: number,
    pathParts: string[]
  ) => {
    switch (segment) {
      case "trainers":
        return t("Trainers");
      case "exercises":
        return t("Exercise Library");
      case "training-programs":
        return t("Training Programs");
      case "workouts":
        if (index > 0 && pathParts.includes("training-programs")) {
          return t("Workouts");
        }
        return t("Individual Workouts");
      case "technique-videos":
        return t("Technique Videos");
      case "dashboard":
        return t("Dashboard");
      case "add":
        return t("Create");
      default:
        if (index > 0) {
          const prevSegment = pathParts[index - 1];
          if (prevSegment === "trainers") {
            return trainerName || "-";
          }
          if (prevSegment === "training-programs") {
            return trainingProgramName || "-";
          }
          if (prevSegment === "workouts") {
            return workoutName || "-";
          }
          if (prevSegment === "exercises") {
            return exerciseName || "-";
          }
          if (prevSegment === "technique-videos") {
            return techniqueVideoName || "-";
          }
        }
        return segment;
    }
  };

  const generateBreadcrumbs = () => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = pathParts
      .filter((part) => part !== "workout-content")
      .map((part, index) => {
        const path = `/${pathParts.slice(0, index + 1).join("/")}`;
        return { label: mapSegmentToName(part, index, pathParts), path };
      });
    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const handleBackClick = () => {
    if (breadcrumbs.length > 1) {
      navigate(breadcrumbs[breadcrumbs.length - 2].path);
    } else {
      navigate(-1);
    }
  };

  return (
    <Header>
      <StyledSpace>
        <BackButton
          type="text"
          icon={<StyledBackButtonIcon />}
          onClick={handleBackClick}
        />
        <StyledBreadcrumb>
          {breadcrumbs.map((breadcrumb, index) => (
            <StyledBreadcrumb.Item
              key={index}
              onClick={() => navigate(breadcrumb.path)}
            >
              {breadcrumb.label}
            </StyledBreadcrumb.Item>
          ))}
        </StyledBreadcrumb>
      </StyledSpace>
    </Header>
  );
};
